import React from "react";
import { Link } from "react-router-dom";
import { imgUrl } from "@/lib/api";
import { JOURNAL_ENTRIES } from "@/data/journal";
import SeoHead from "@/components/site/SeoHead";

const formatDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("en-IN", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return iso;
  }
};

const Journal = () => {
  const [feature, ...rest] = JOURNAL_ENTRIES;

  return (
    <div className="pt-24 md:pt-40 pb-24 md:pb-32" data-testid="journal-page">
      <SeoHead
        title="The Journal · notes from the table"
        description="Essays from MELAVI on slow-fashion, hand-embroidery, cotton, and the craft of making clothes that outlast the child."
        path="/journal"
      />
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-24">
        <div className="grid md:grid-cols-12 gap-6 md:gap-12 mb-10 md:mb-16 items-end">
          <div className="md:col-span-6">
            <div className="nav-item mb-3 md:mb-4">The Journal</div>
            <h1 className="serif text-4xl md:text-7xl leading-[1.02]" data-testid="journal-title">
              Notes from the <em className="italic">table</em>.
            </h1>
          </div>
          <p className="md:col-span-5 md:col-start-8 md:self-end text-ink/80 leading-relaxed text-sm md:text-base">
            Small essays from Meghana and Lallitha on the pieces, the process,
            and the days that shape both. Written between batches, mostly on Tuesdays.
          </p>
        </div>

        {/* Feature — first entry as a large editorial hero */}
        <Link
          to={`/journal/${feature.slug}`}
          className="group block hairline-b pb-14 mb-14 md:mb-20 grid md:grid-cols-12 gap-8 md:gap-14"
          data-testid={`journal-feature-${feature.slug}`}
        >
          <div className="md:col-span-7 overflow-hidden">
            <img
              src={imgUrl(feature.heroImage || feature.image)}
              alt={feature.title}
              className="w-full aspect-[16/10] md:aspect-[4/3] object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.03]"
            />
          </div>
          <div className="md:col-span-5 md:self-center">
            <div className="nav-item mb-3" style={{ color: "var(--accent)" }}>
              {feature.kicker} · Feature
            </div>
            <h2 className="serif text-3xl md:text-5xl leading-[1.05]">{feature.title}</h2>
            <p className="mt-5 text-ink/80 leading-relaxed text-sm md:text-[15px]">{feature.dek}</p>
            <div className="mt-6 nav-item text-ink/60">
              {feature.author} · {formatDate(feature.date)} · {feature.read} read
            </div>
            <div className="mt-6 crimson-link nav-item">Read the essay →</div>
          </div>
        </Link>

        {/* Rest — staggered editorial grid */}
        <div className="grid md:grid-cols-3 gap-8 md:gap-12" data-testid="journal-grid">
          {rest.map((e, i) => (
            <Link
              key={e.slug}
              to={`/journal/${e.slug}`}
              className={`group ${i === 1 ? "md:mt-16" : i === 2 ? "md:mt-32" : ""}`}
              data-testid={`journal-tile-${e.slug}`}
            >
              <div className="overflow-hidden bg-sand">
                <img
                  src={imgUrl(e.image)}
                  alt={e.title}
                  loading="lazy"
                  className="w-full aspect-[4/5] object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-[1.03]"
                />
              </div>
              <div className="mt-5">
                <div className="nav-item text-ink/60 mb-2">
                  {e.kicker} · {e.read} read
                </div>
                <h3 className="serif text-2xl md:text-3xl leading-tight">{e.title}</h3>
                <p className="mt-3 text-ink/70 leading-relaxed text-sm">{e.excerpt}</p>
                <div className="mt-4 text-[10px] tracking-[0.24em] uppercase text-ink/50">
                  {e.author} · {formatDate(e.date)}
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Journal;
